// Los PERMISOS del Chat: qué puede hacer Atlas solo y qué te pregunta antes —
// ADR-2026-09-08-permisos-de-generacion-del-chat y
// ADR-2026-09-08-permisos-tambien-para-el-catalogo.
//
// Hasta acá la regla era fija y estaba escrita en varios lugares: toda acción del agente
// que gastara créditos o tocara datos mostraba una card y esperaba el click. Es el default
// correcto —lo decidió un modelo— pero para el que ya confía es un click de más cada vez.
//
// Son permisos POR ACCIÓN y no un único interruptor de confianza, porque las acciones se
// equivocan distinto: una imagen de más son 1 o 2 créditos, un video es lo más caro y lento
// de rehacer, un producto mal creado queda en el catálogo hasta que alguien lo borra a mano,
// y la investigación dispara scrapers que se cobran aunque no traigan nada.
//
// Todo PURO, para poder probarlo.

/** Qué se está por hacer. */
export type TipoAccion = 'imagen' | 'video' | 'lote' | 'productos' | 'investigacion';
export const TIPOS_ACCION: TipoAccion[] = [
  'imagen',
  'video',
  'lote',
  'productos',
  'investigacion',
];

/** La generación tiene tres niveles porque el video merece su propio escalón. */
export type NivelConfirmacion = 'siempre' | 'solo_videos' | 'nunca';
export const NIVELES_CONFIRMACION: NivelConfirmacion[] = ['siempre', 'solo_videos', 'nunca'];

/** El resto son dos estados: preguntar o hacerlo directo. */
export type PermisoAccion = 'preguntar' | 'directo';

export interface PermisosChat {
  /** Imágenes y videos (sueltos o en lote). */
  generacion: NivelConfirmacion;
  /** Sumar productos al catálogo a partir de fotos (`agregar_productos`). */
  productos: PermisoAccion;
  /** Habilitar los scrapers de datos reales (`activar_investigacion`). */
  investigacion: PermisoAccion;
}

/** El default es el comportamiento de toda la vida: nada pasa sin un click. */
export const PERMISOS_POR_DEFECTO: PermisosChat = {
  generacion: 'siempre',
  productos: 'preguntar',
  investigacion: 'preguntar',
};

export const esNivelConfirmacion = (v: unknown): v is NivelConfirmacion =>
  typeof v === 'string' && (NIVELES_CONFIRMACION as string[]).includes(v);

export const esPermisoAccion = (v: unknown): v is PermisoAccion =>
  v === 'preguntar' || v === 'directo';

/** ¿Hay que mostrar la card y esperar el click? */
export function pideConfirmacion(permisos: PermisosChat, tipo: TipoAccion): boolean {
  if (tipo === 'productos') return permisos.productos !== 'directo';
  if (tipo === 'investigacion') return permisos.investigacion !== 'directo';
  switch (permisos.generacion) {
    case 'nunca':
      return false;
    case 'solo_videos':
      // Aunque confíe en el agente para las imágenes, el video es lo más caro y lo más
      // lento de rehacer: ahí quiere ver el resumen con el costo antes de largarlo.
      return tipo === 'video';
    default:
      return true;
  }
}

/** Lo contrario, que es lo que leen el motor y las cards. */
export const sinConfirmacion = (permisos: PermisosChat, tipo: TipoAccion): boolean =>
  !pideConfirmacion(permisos, tipo);

/** Qué acción es cada tool del agente. `null` = no pasa por permisos. */
export function accionDeTool(tool: string): TipoAccion | null {
  switch (tool) {
    case 'generate_image':
      return 'imagen';
    case 'generate_video':
      return 'video';
    case 'generar_lote_imagenes':
      return 'lote';
    case 'agregar_productos':
      return 'productos';
    case 'activar_investigacion':
      return 'investigacion';
    default:
      return null;
  }
}

// ── El botón blanco de la card: «… sin preguntarme» ─────────────────────────
//
// ADR-2026-09-13-generar-sin-preguntarme-en-la-card. Hasta acá los permisos se cambiaban
// en un solo lugar —la hoja de Permisos, tres pantallas adentro del menú "+"— y el que
// estaba cansado de confirmar tenía que ir a buscarla sabiendo que existe. El botón hace
// las dos cosas de una: ejecuta ESTA acción y deja el permiso puesto para las próximas.
//
// El cambio lo hace el USUARIO tocando el botón, nunca el agente: la tool
// `configurar_preferencias` puede DEVOLVER las confirmaciones, no sacarlas
// (ADR-2026-09-13-el-agente-configura-las-preferencias).

/** El permiso que queda después de tocar el botón, con el texto del botón. */
export interface CambioDePermiso {
  clave: keyof PermisosChat;
  valor: NivelConfirmacion | PermisoAccion;
  /** El verbo del botón PRIMARIO de esa card: "Generar", "Crear", "Activar". */
  etiqueta: string;
}

/**
 * Qué permiso deja tocar «sin preguntarme» en una card de este tipo.
 *
 * La generación es UN nivel de tres, así que el escalón importa: desde una IMAGEN queda
 * `solo_videos` —las imágenes salen solas, el video sigue preguntando— porque un video de
 * más es lo más caro y lo más lento de rehacer, y nadie que apaga la confirmación de una
 * imagen de 1 crédito está pidiendo eso para un video. Desde un VIDEO sí queda `nunca`:
 * el que no quiere confirmar lo caro, menos va a querer confirmar lo barato.
 */
export function permisoParaNoPreguntar(tipo: TipoAccion): CambioDePermiso {
  switch (tipo) {
    case 'video':
      return { clave: 'generacion', valor: 'nunca', etiqueta: 'Generar sin preguntarme' };
    case 'productos':
      return { clave: 'productos', valor: 'directo', etiqueta: 'Crear sin preguntarme' };
    case 'investigacion':
      return {
        clave: 'investigacion',
        valor: 'directo',
        etiqueta: 'Activar sin preguntarme',
      };
    default:
      return { clave: 'generacion', valor: 'solo_videos', etiqueta: 'Generar sin preguntarme' };
  }
}

// ── Lo que se ve en la hoja de Permisos ─────────────────────────────────────

export const ETIQUETAS_CONFIRMACION: Record<
  NivelConfirmacion,
  { titulo: string; detalle: string }
> = {
  siempre: {
    titulo: 'Preguntar siempre',
    detalle: 'Cada imagen y cada video te muestran el resumen y vos tocás Generar.',
  },
  solo_videos: {
    titulo: 'Preguntar solo videos',
    detalle: 'Las imágenes salen solas; los videos, que son más caros, te preguntan.',
  },
  nunca: {
    titulo: 'No preguntar nunca',
    detalle: 'Atlas genera apenas lo decide. Los créditos se descuentan sin confirmar.',
  },
};

/** Las acciones que no son generación, con lo que implica dejarlas sueltas. */
export const ACCIONES_SUELTAS: Array<{
  clave: 'productos' | 'investigacion';
  titulo: string;
  detalle: string;
  /** Lo que hay que saber ANTES de ponerlo en directo. */
  advertencia: string;
}> = [
  {
    clave: 'productos',
    titulo: 'Sumar productos al catálogo',
    detalle: 'Cuando le pedís que agregue fotos como productos.',
    advertencia:
      'En directo los crea con los nombres y el agrupado que propone la IA, sin que los revises.',
  },
  {
    clave: 'investigacion',
    titulo: 'Investigar datos reales',
    detalle: 'Instagram, Facebook, Google Maps y tiendas online.',
    advertencia: 'En directo arranca sola. Consume créditos aunque no encuentre nada.',
  },
];

/**
 * La línea que le avisa al MODELO cómo se comportan las cards en este turno. Sin esto
 * sigue diciendo "tocá Generar" sobre una imagen que ya salió sola, que es la peor mezcla:
 * el usuario lee una instrucción que no aplica y no entiende qué pasó.
 */
export function lineaConfirmacionParaPrompt(permisos: PermisosChat): string {
  const partes: string[] = [];
  switch (permisos.generacion) {
    case 'nunca':
      partes.push(
        '• Confirmación de generaciones: APAGADA. El usuario eligió que no le preguntes: las imágenes y los videos se generan SOLOS apenas mostrás la card. NO le pidas que toque Generar ni digas que le dejás el resumen — avisá que ya está generando y que tarda un rato.',
      );
      break;
    case 'solo_videos':
      partes.push(
        '• Confirmación de generaciones: SÓLO VIDEOS. Las imágenes (sueltas y en lote) se generan SOLAS: no digas "tocá Generar", avisá que ya está generando. El VIDEO sí espera el click: ahí sí invitá a confirmar viendo el costo.',
      );
      break;
    default:
      partes.push(
        '• Confirmación de generaciones: SIEMPRE. Ni la imagen ni el video se generan solos: la card muestra el resumen y el usuario toca Generar. Invitá a confirmar y NUNCA anuncies que ya estás generando.',
      );
  }
  partes.push(
    permisos.productos === 'directo'
      ? '• Sumar productos al catálogo: DIRECTO. `agregar_productos` crea sin que el usuario revise la propuesta. NUNCA escribas "revisá el nombre" ni "tocá Crear": no hay botón que tocar y quedaría debajo de una card que ya dice LISTO. Antes de la tool, avisá que los vas a sumar; después del resultado, contá cuáles quedaron. Como nadie va a revisar los nombres, poné el que dijo el usuario si lo dijo.'
      : '• Sumar productos al catálogo: CON CONFIRMACIÓN. La card muestra la propuesta editable y el usuario toca Crear. No digas que ya está creado.',
  );
  partes.push(
    permisos.investigacion === 'directo'
      ? '• Investigación de datos reales: DIRECTA. `activar_investigacion` se acepta sola, sin card. Igual consume créditos: usala sólo cuando de verdad haga falta y avisá en una línea antes de largarte.'
      : '• Investigación de datos reales: CON CONFIRMACIÓN. `activar_investigacion` muestra una card y el usuario acepta o cancela.',
  );
  if (
    permisos.generacion === 'nunca' ||
    permisos.productos === 'directo' ||
    permisos.investigacion === 'directo'
  ) {
    partes.push(
      '• OJO: con estos permisos hay acciones sin red de seguridad. Asegurate del pedido ANTES de llamar la tool: si algo importante está en duda, preguntá en texto primero.',
    );
  }
  return partes.join('\n');
}
